import { Lock } from 'lucide-react';

import { Badge, Calendario, Input, Label } from '@/components/ui';
import type { QuestionarioPergunta, TipoResposta } from '@/lib/questionarios-store';

/**
 * `<PerguntaTecnicoInput>` — componente que renderiza o input por tipo do
 * questionário estruturado v2 (Bloco B).
 *
 * **FASE-C (copy):** foto, anexo e assinatura ainda não têm upload, e a tela
 * dizia isso com jargão de dev — `Tipo "anexo" em desenvolvimento — capture
 * via fallback texto livre por enquanto:`. O técnico em campo não sabe o que é
 * "fallback". Agora o campo se explica pelo que ele FAZ: descrever ou colar o
 * link. Também trocou o emoji 🔒 do selo "Interna" pelo ícone Lucide.
 *
 * Helper de visibilidade condicional vive em `./pergunta-visivel.ts`.
 */

const TIPO_SEM_UPLOAD_LABEL: Partial<Record<TipoResposta, string>> = {
  foto: 'Foto',
  anexo: 'Anexo',
  assinatura_inline: 'Assinatura',
};

interface PerguntaTecnicoInputProps {
  pergunta: QuestionarioPergunta;
  value: unknown;
  onChange: (v: unknown) => void;
}

export function PerguntaTecnicoInput({ pergunta, value, onChange }: PerguntaTecnicoInputProps) {
  const labelEl = (
    <div className="flex items-baseline gap-1.5 mb-1 flex-wrap">
      <Label className="!mb-0">
        {pergunta.codigo}. {pergunta.pergunta}
        {pergunta.obrigatoria && <span className="text-red ml-0.5">*</span>}
      </Label>
      {!pergunta.isPublic && (
        <Badge variant="danger" size="sm">
          <Lock className="h-3 w-3 shrink-0" aria-hidden />
          Interna
        </Badge>
      )}
    </div>
  );

  return (
    <div data-testid={`pergunta-tecnico-${pergunta.codigo}`}>
      {labelEl}
      {renderInput(pergunta.tipoResposta, pergunta, value, onChange)}
    </div>
  );
}

function renderInput(
  tipo: TipoResposta,
  pergunta: QuestionarioPergunta,
  value: unknown,
  onChange: (v: unknown) => void
) {
  switch (tipo) {
    case 'sim_nao':
      return (
        <div className="flex gap-3">
          {['Sim', 'Não'].map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name={`p-${pergunta.codigo}`}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    case 'texto_curto':
      return (
        <Input
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'texto_longo':
      return (
        <textarea
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full border border-line dark:border-line-dark rounded-lg px-3 py-2 text-sm bg-paper-surface dark:bg-paper-dark-surface"
        />
      );
    case 'numero':
      return (
        <Input
          type="number"
          value={value !== undefined && value !== null ? String(value) : ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'unica_escolha':
      return (
        <div className="space-y-1">
          {pergunta.opcoes.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="radio"
                name={`p-${pergunta.codigo}`}
                value={opt}
                checked={value === opt}
                onChange={() => onChange(opt)}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    case 'multipla_escolha': {
      const sel = Array.isArray(value) ? (value as string[]) : [];
      return (
        <div className="space-y-1">
          {pergunta.opcoes.map((opt) => (
            <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={sel.includes(opt)}
                onChange={(e) =>
                  onChange(e.target.checked ? [...sel, opt] : sel.filter((s) => s !== opt))
                }
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    case 'rating_1_5':
      return (
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange(n)}
              className={
                'w-9 h-9 rounded-lg border text-sm font-medium transition-colors ' +
                (value === n
                  ? 'bg-red text-white border-red'
                  : 'border-line dark:border-line-dark text-ink-2 hover:bg-paper-warm dark:hover:bg-paper-dark-warm')
              }
            >
              {n}
            </button>
          ))}
        </div>
      );
    case 'data_hora':
      return (
        <Calendario
          valor={typeof value === 'string' ? value : ''}
          onChange={onChange}
          placeholder="Escolher data e hora"
          testId="pergunta-data-hora-calendario"
        />
      );
    case 'foto':
    case 'anexo':
    case 'assinatura_inline':
      return (
        <div className="min-w-0">
          <Input
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Descreva ou cole o link"
            hint={`${TIPO_SEM_UPLOAD_LABEL[tipo] ?? 'Este item'} ainda não sobe direto pelo app. Descreva aqui ou cole o link do arquivo.`}
          />
        </div>
      );
    default:
      return (
        <Input
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
}
